package ui.campaign.detailv2.editor.wrapper

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import api.CampaignType
import api.ResourceOrientation
import api.ResourceType
import common.frontend.api.Io
import org.ssoft.common.type.AspectRatio
import org.ssoft.common.type.ScreenRatio
import tools.EFieldList
import ui.resource.ResourceListingArgs
import kotlin.math.roundToLong

abstract class ImplCampaignWrapperEVisual(
    private val companyId: Long,
    protected val campaign: Io.Campaign,
    groups: List<Io.Campaign.VisualGroup>,
    private val devicesScreenRatio: () -> List<ScreenRatio>
) : ImplCampaignWrapperE {

    protected val group = AdapterGroup(companyId, campaign, groups)

    var combinationToCheck: Pair<AdapterVisual.Item, Io.Campaign.ResItem>? by mutableStateOf(null)

    val deviceRatioMasks get() = devicesScreenRatio().map { dsr -> dsr.mask }.toHashSet()

    abstract fun extract(): List<Io.Campaign.VisualGroup>

    fun generatePickerArgs() {
        combinationToCheck = null
        group.state.forEach { group ->
            group.visuals.state.forEach {
                it.generatePickerArgs(companyId, campaign)
            }
        }
    }

    class AdapterGroup(
        private val companyId: Long,
        private val campaign: Io.Campaign,
        items: List<Io.Campaign.VisualGroup>,
    ) : EFieldList<Io.Campaign.VisualGroup, AdapterGroup.Item>(
        items = items.toMutableList(),
        onBindEntry = { item ->
            Item(companyId, campaign, data = item)
        }
    ) {
        class Item(
            companyId: Long,
            campaign: Io.Campaign,
            override val data: Io.Campaign.VisualGroup,
            override val key: String = data.id.toString(),
        ) : Entry<Io.Campaign.VisualGroup> {

            val visuals = AdapterVisual(companyId, campaign, data)
            val conditions = ImplCampaignWrapperEConditionHeader(campaign, data.conditions)
        }
    }

    class AdapterVisual(
        private val companyId: Long,
        private val campaign: Io.Campaign,
        val group: Io.Campaign.VisualGroup
    ) : EFieldList<ScreenRatio, AdapterVisual.Item>(
        items = ScreenRatio.all.sortedWith(compareBy({ it.land }, { Int.MAX_VALUE - it.mask })).toMutableList(),
        onBindEntry = { item ->
            Item(
                data = item,
                visualGroup = { group }
            ).apply {
                generatePickerArgs(companyId, campaign)
            }
        }
    ) {
        class Item(
            override val data: ScreenRatio,
            override val key: String = "${data.pair.first} : ${data.pair.second}",
            private val visualGroup: () -> Io.Campaign.VisualGroup
        ) : Entry<ScreenRatio> {

            var pickerKey: String = "-"

            private var isCommercial = false

            var pickerArgs: ResourceListingArgs = ResourceListingArgs()
                private set

            var changed = false
                private set

            var invalidVisualDuration by mutableStateOf(false)
                private set

            var resource by mutableStateOf(visualGroup().res.firstOrNull { it.screenRatio == data.mask })
                private set

            fun isVisualDurationOk(newVisual: Io.ListingResource.Item): Boolean {
                return ResourceType.IMAGE match newVisual.type ||
                        !isCommercial ||
                        (ResourceType.VIDEO match newVisual.type && newVisual.duration in pickerArgs.restrictionDurationMin..pickerArgs.restrictionDurationMax)
            }

            fun createVisualEntry(newResource: Io.ListingResource.Item): Io.Campaign.ResItem {
                return Io.Campaign.ResItem(
                    id = newResource.id,
                    conditionId = visualGroup().id,
                    resourceId = newResource.id,
                    name = newResource.name,
                    duration = newResource.duration,
                    type = newResource.type,
                    aspectRatio = data.toAspectRatio().mask,
                    isHorizontal = data.land,
                    width = newResource.width,
                    height = newResource.height,
                    isCustom = true,
                    screenRatio = data.mask
                )
            }

            fun set(newVisual: Io.Campaign.ResItem) {
                resource = newVisual
                changed = true
                validateVisualDuration()
            }

            fun reset() {
                resource = null
                changed = true
                validateVisualDuration()
            }

            fun generatePickerArgs(companyId: Long, campaign: Io.Campaign) {

                isCommercial = campaign.isCommercial

                val (durationMin, durationMax) = run {

                    val offset = (campaign.settings.campaignDuration.toFloat() * 0.10F).roundToLong()

                    val base = when (CampaignType[campaign.settings.type]) {
                        CampaignType.SLOT -> campaign.settings.campaignDuration * campaign.settings.campaignCount
                        CampaignType.IMPRESSION -> campaign.settings.campaignDuration
                        CampaignType.NONE -> 0
                    }

                    val min = (base - offset)
                    val max = (base + offset)

                    min to max
                }

                pickerArgs = ResourceListingArgs(
                    companyId = companyId,
                    pickerMode = true,
                    restrictionType = ResourceType.VIDEO.mask + ResourceType.IMAGE.mask,
                    restrictionOrientation =
                        if (data.land) ResourceOrientation.HORIZONTAL.mask
                        else ResourceOrientation.VERTICAL.mask,
                    restrictionAspectRatio =
                        if (campaign.settings.campaignVisualIgnoreRatio) AspectRatio.NONE.mask
                        else data.toAspectRatio().mask,
                    restrictionDurationMax = durationMax,
                    restrictionDurationMin = durationMin,
                    restrictionCanIgnoreAspectRatio = campaign.settings.campaignVisualIgnoreRatio ||
                            !campaign.settings.enableCommercial,
                    restrictionCanIgnoreDuration = campaign.settings.campaignVisualIgnoreDuration ||
                            !campaign.settings.enableCommercial,
                    excluded = ""
                )

                validateVisualDuration()
                pickerKey = "$campaign:visual:${visualGroup().id}:${visualGroup().isPrimary}:${data.mask}"
            }

            private fun validateVisualDuration() {
                invalidVisualDuration = resource
                    ?.takeIf { isCommercial }
                    ?.takeIf { ResourceType.VIDEO match it.type }
                    ?.let { it.duration !in pickerArgs.restrictionDurationMin..pickerArgs.restrictionDurationMax } == true
            }
        }
    }
}